/**
 * Eventos de swipe suportados.
 * @typedef {('SWIPE_UP'|'SWIPE_DOWN'|'SWIPE_LEFT'|'SWIPE_RIGHT')} SwipeEvent
 */
export type SwipeEvent = 'SWIPE_UP' | 'SWIPE_DOWN' | 'SWIPE_LEFT' | 'SWIPE_RIGHT'

/**
 * Tipo de callback para eventos de swipe.
 * @typedef {Function} SwipeCallback
 * @param {unknown} [data] - Dados opcionais passados ao callback
 * @returns {void}
 */
export type SwipeCallback = (data?: unknown) => void

/**
 * Mapa de eventos e callbacks.
 * @typedef {Object.<SwipeEvent, SwipeCallback[]>} EventMap
 * @private
 */
type EventMap = Record<SwipeEvent, SwipeCallback[]>

/**
 * Coordenadas de um ponto.
 * @typedef {Object} Point
 * @property {number} x - Coordenada X
 * @property {number} y - Coordenada Y
 * @private
 */
interface Point {
  x: number
  y: number
}

/**
 * UniversalSwipe - Detecção de gestos de swipe com suporte universal.
 *
 * Estratégia de detecção (prioridade):
 * 1. Pointer Events - API moderna universal (Chrome 55+, Firefox 59+, Safari 13+, Edge 12+)
 * 2. Touch Events - Fallback para dispositivos móveis sem Pointer Events
 * 3. Mouse Events - Fallback para navegadores antigos
 *
 * Compatibilidade:
 * - ✅ Firefox Desktop (mouse events)
 * - ✅ Chrome Desktop/Mobile (pointer/touch events)
 * - ✅ Safari Desktop/Mobile (pointer/touch events)
 * - ✅ Edge (pointer events)
 * - ✅ Navegadores antigos (touch/mouse events)
 *
 */
export class UniversalSwipe {
  /**
   * Mapa de eventos e seus callbacks.
   */
  private evtMap: EventMap = {
    SWIPE_UP: [],
    SWIPE_DOWN: [],
    SWIPE_LEFT: [],
    SWIPE_RIGHT: [],
  }

  /**
   * Posição inicial do gesto.
   */
  private startPoint: Point | null = null

  /**
   * Elemento alvo do swipe.
   */
  private element: HTMLElement

  /**
   * Tipo de evento sendo usado (pointer, touch ou mouse).
   */
  private eventType: 'pointer' | 'touch' | 'mouse' = 'touch'

  /**
   * Flag indicando se o gesto está ativo.
   */
  private isActive: boolean = false

  /**
   * Pointer ID para captura de eventos (usado com Pointer Events).
   */
  private pointerId: number | null = null

  /**
   * Limiar mínimo de movimento para detectar swipe (em pixels).
   */
  private readonly threshold: number = 30

  /**
   * Direção do swipe esperado ('horizontal' | 'vertical' | 'both').
   */
  private swipeDirection: 'horizontal' | 'vertical' | 'both' = 'both'

  /**
   * Bound handlers para remoção de listeners.
   */
  private boundHandleStart: (evt: PointerEvent | TouchEvent | MouseEvent) => void
  private boundHandleEnd: (evt: PointerEvent | TouchEvent | MouseEvent) => void
  private boundHandleCancel: (evt: PointerEvent | TouchEvent | MouseEvent) => void

  /**
   * Cria uma nova instância de UniversalSwipe.
   * @constructor
   * @param {HTMLElement} element - Elemento HTML que receberá os gestos de swipe
   * @param {Object} [options] - Opções de configuração
   * @param {'horizontal'|'vertical'|'both'} [options.direction='both'] - Direção do swipe a ser detectado
   */
  constructor(element: HTMLElement, options?: { direction?: 'horizontal' | 'vertical' | 'both' }) {
    this.element = element
    this.swipeDirection = options?.direction || 'both'

    this.boundHandleStart = this.handleStart.bind(this)
    this.boundHandleEnd = this.handleEnd.bind(this)
    this.boundHandleCancel = this.handleCancel.bind(this)

    this.applyCSSFix()

    this.detectAndAttachListeners()
  }

  /**
   * Aplica CSS touch-action para prevenir gestos nativos do browser.
   * Usa 'pan-y' para permitir scroll vertical enquanto detecta swipes horizontais.
   * Usa 'pan-x' para permitir scroll horizontal enquanto detecta swipes verticais.
   * @private
   * @returns {void}
   */
  private applyCSSFix(): void {
    // Configura touch-action baseado na direção do swipe
    if (this.swipeDirection === 'horizontal') {
      // Permite scroll vertical, bloqueia horizontal para detectar SWIPE_LEFT/RIGHT
      this.element.style.touchAction = 'pan-y'
    } else if (this.swipeDirection === 'vertical') {
      // Permite scroll horizontal, bloqueia vertical para detectar SWIPE_UP/DOWN
      this.element.style.touchAction = 'pan-x'
    } else {
      // Bloqueia scroll em ambas direções para detectar qualquer swipe
      // Use 'manipulation' se houver elementos clicáveis dentro
      this.element.style.touchAction = 'manipulation'
    }
  }

  /**
   * Detecta qual tipo de evento usar e anexa os listeners apropriados.
   * Estratégia: Prioriza Pointer Events (universal), depois Touch, depois Mouse.
   * @private
   * @returns {void}
   */
  private detectAndAttachListeners(): void {
    // 1. Pointer Events: API moderna e universal (Chrome 55+, Firefox 59+, Safari 13+)
    if (window.PointerEvent) {
      this.eventType = 'pointer'
      this.element.addEventListener('pointerdown', this.boundHandleStart)
      this.element.addEventListener('pointerup', this.boundHandleEnd)
      this.element.addEventListener('pointercancel', this.boundHandleCancel)
    }
    // 2. Touch Events: Fallback para navegadores sem Pointer Events
    else if ('ontouchstart' in window && navigator.maxTouchPoints > 0) {
      this.eventType = 'touch'
      // Passive: false permite preventDefault()
      this.element.addEventListener('touchstart', this.boundHandleStart, { passive: false })
      this.element.addEventListener('touchend', this.boundHandleEnd, { passive: false })
      this.element.addEventListener('touchcancel', this.boundHandleCancel, { passive: false })
    }
    // 3. Mouse Events: Fallback final para navegadores antigos
    else {
      this.eventType = 'mouse'
      this.element.addEventListener('mousedown', this.boundHandleStart)
      // mousemove e mouseup são adicionados ao document no handleStart
    }
  }

  /**
   * Remove todos os event listeners do elemento.
   * @private
   * @returns {void}
   */
  private detachListeners(): void {
    if (this.eventType === 'pointer') {
      this.element.removeEventListener('pointerdown', this.boundHandleStart)
      this.element.removeEventListener('pointerup', this.boundHandleEnd)
      this.element.removeEventListener('pointercancel', this.boundHandleCancel)
    } else if (this.eventType === 'touch') {
      this.element.removeEventListener('touchstart', this.boundHandleStart)
      this.element.removeEventListener('touchend', this.boundHandleEnd)
      this.element.removeEventListener('touchcancel', this.boundHandleCancel)
    } else {
      this.element.removeEventListener('mousedown', this.boundHandleStart)
      this.removeDocumentMouseListeners()
    }
  }

  /**
   * Adiciona listeners de mouse ao document (necessário para rastrear fora do elemento).
   * @private
   * @returns {void}
   */
  private addDocumentMouseListeners(): void {
    document.addEventListener('mouseup', this.boundHandleEnd)
    document.addEventListener('mouseleave', this.boundHandleCancel)
  }

  /**
   * Remove listeners de mouse do document.
   * @private
   * @returns {void}
   */
  private removeDocumentMouseListeners(): void {
    document.removeEventListener('mouseup', this.boundHandleEnd)
    document.removeEventListener('mouseleave', this.boundHandleCancel)
  }

  /**
   * Extrai coordenadas do evento independente do tipo.
   * @private
   * @param {PointerEvent|TouchEvent|MouseEvent} evt - Evento de pointer, touch ou mouse
   * @returns {Point} Coordenadas {x, y} do evento
   */
  private getCoordinates(evt: PointerEvent | TouchEvent | MouseEvent): Point {
    if ('touches' in evt && evt.touches.length > 0) {
      // Touch Event
      return {
        x: evt.touches[0].clientX,
        y: evt.touches[0].clientY,
      }
    } else if ('changedTouches' in evt && evt.changedTouches.length > 0) {
      // Touch Event (touchend)
      return {
        x: evt.changedTouches[0].clientX,
        y: evt.changedTouches[0].clientY,
      }
    } else if ('clientX' in evt && 'clientY' in evt) {
      // Pointer Event ou Mouse Event
      return {
        x: evt.clientX,
        y: evt.clientY,
      }
    }

    return { x: 0, y: 0 }
  }

  /**
   * Handler do início do gesto (pointerdown, touchstart, mousedown).
   * @private
   * @param {PointerEvent|TouchEvent|MouseEvent} evt - Evento do browser
   * @returns {void}
   */
  private handleStart(evt: PointerEvent | TouchEvent | MouseEvent): void {
    this.isActive = true
    this.startPoint = this.getCoordinates(evt)

    // Para Pointer Events, captura o pointer para rastrear fora do elemento
    if (this.eventType === 'pointer' && 'pointerId' in evt) {
      this.pointerId = evt.pointerId
      try {
        this.element.setPointerCapture(evt.pointerId)
      } catch {
        // Ignora erro se captura não for suportada
      }
    }

    // Para Mouse Events, adiciona listeners ao document
    if (this.eventType === 'mouse') {
      this.addDocumentMouseListeners()
    }
  }

  /**
   * Handler do fim do gesto (pointerup, touchend, mouseup).
   * @private
   * @param {PointerEvent|TouchEvent|MouseEvent} evt - Evento do browser
   * @returns {void}
   */
  private handleEnd(evt: PointerEvent | TouchEvent | MouseEvent): void {
    if (!this.isActive || this.startPoint === null) {
      return
    }

    const endPoint = this.getCoordinates(evt)
    const deltaX = endPoint.x - this.startPoint.x
    const deltaY = endPoint.y - this.startPoint.y

    // Threshold para evitar detecção acidental
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(deltaY)

    // Só dispara swipe se movimento for maior que threshold
    if (absDeltaX < this.threshold && absDeltaY < this.threshold) {
      this.cleanup()
      return
    }

    // Determina direção do swipe baseado no maior delta
    if (absDeltaX > absDeltaY) {
      // Swipe horizontal
      if (deltaX < 0) {
        this.trigger('SWIPE_LEFT')
      } else {
        this.trigger('SWIPE_RIGHT')
      }
    } else {
      // Swipe vertical
      if (deltaY < 0) {
        this.trigger('SWIPE_UP')
      } else {
        this.trigger('SWIPE_DOWN')
      }
    }

    this.cleanup()
  }

  /**
   * Handler de cancelamento do gesto (pointercancel, touchcancel, mouseleave).
   * @private
   * @param {PointerEvent|TouchEvent|MouseEvent} _evt - Evento do browser (não utilizado)
   * @returns {void}
   */
  private handleCancel(_evt: PointerEvent | TouchEvent | MouseEvent): void {
    this.cleanup()
  }

  /**
   * Limpa o estado do gesto e remove listeners temporários.
   * @private
   * @returns {void}
   */
  private cleanup(): void {
    this.isActive = false
    this.startPoint = null

    // Para Pointer Events, libera a captura
    if (this.eventType === 'pointer' && this.pointerId !== null) {
      try {
        this.element.releasePointerCapture(this.pointerId)
      } catch {
        // Ignora erro se pointer já foi liberado
      }
      this.pointerId = null
    }

    if (this.eventType === 'mouse') {
      this.removeDocumentMouseListeners()
    }
  }

  /**
   * Registra um callback para um evento de swipe.
   * @param evt - Nome do evento ('SWIPE_UP', 'SWIPE_DOWN', 'SWIPE_LEFT', 'SWIPE_RIGHT')
   * @param cb - Função callback a ser executada quando o evento ocorrer
   */
  public on(evt: SwipeEvent, cb: SwipeCallback): void {
    this.evtMap[evt].push(cb)
  }

  /**
   * Remove um callback de um evento de swipe.
   * @param evt - Nome do evento
   * @param cb - Função callback a ser removida
   */
  public off(evt: SwipeEvent, cb: SwipeCallback): void {
    this.evtMap[evt] = this.evtMap[evt].filter((callback) => callback !== cb)
  }

  /**
   * Dispara um evento de swipe, executando todos os callbacks registrados.
   * @private
   * @param {SwipeEvent} evt - Nome do evento a ser disparado
   * @param {unknown} [data] - Dados opcionais a serem passados aos callbacks
   * @returns {void}
   */
  private trigger(evt: SwipeEvent, data?: unknown): void {
    this.evtMap[evt].forEach((handler) => handler(data))
  }

  /**
   * Destrói a instância, removendo todos os event listeners.
   * Deve ser chamado quando o elemento for removido do DOM.
   * Remove o CSS touch-action aplicado no construtor.
   * @public
   * @returns {void}
   * @example
   * ```typescript
   * disconnectedCallback() {
   *   this.swipe?.destroy();
   * }
   * ```
   */
  public destroy(): void {
    this.cleanup()
    this.detachListeners()
    this.element.style.touchAction = ''
    this.evtMap = {
      SWIPE_UP: [],
      SWIPE_DOWN: [],
      SWIPE_LEFT: [],
      SWIPE_RIGHT: [],
    }
  }
}

export default UniversalSwipe
